<script lang="ts" setup>
import { computed } from 'vue';
import { Reason } from '@ilteducation/customer-mgmt-permissions';
import { useLocale } from '../composables/useLocale';
import { useEscalateLogin } from '../composables/useEscalateLogin';
import { isFixableByEscalatedLogin } from '../helpers/permissions';

type Props = {
  /**
   * A `Reason`, or falsy when there is no denial to explain. Not typed as `Reason` because callers
   * hold their check result in a `ref()`, whose `UnwrapRef` widens the result union's `reason` to
   * `unknown` under `strict: false`. Narrowed below.
   */
  reason?: unknown;
  icon?: string;
  color?: string;
};

const props = withDefaults(defineProps<Props>(), {
  icon: 'mdi-lock',
});

const { t } = useLocale();
const { requestEscalatedLogin } = useEscalateLogin();

const reason = computed(() => (props.reason || undefined) as Reason | undefined);

const isActionable = computed(() => isFixableByEscalatedLogin(reason.value));

const text = computed(() => {
  if (!reason.value) {
    return '';
  }
  const reasonText = t(`lcp.permissions.${reason.value}`);
  return isActionable.value ? `${reasonText} ${t('lcp.permissions.escalate_login_hint')}` : reasonText;
});

const onActivate = () => {
  if (isActionable.value) {
    requestEscalatedLogin();
  }
};
</script>

<template>
  <v-tooltip v-if="text" :text="text" location="top" interactive>
    <template #activator="{ props: activatorProps }">
      <v-icon
        :icon="icon"
        :color="color ?? (isActionable ? 'primary' : undefined)"
        :class="{ 'permission-lock--actionable': isActionable }"
        :role="isActionable ? 'button' : 'img'"
        :tabindex="isActionable ? 0 : undefined"
        :aria-label="text"
        v-bind="{ ...activatorProps, ...$attrs }"
        @click.stop="onActivate"
        @keydown.enter.stop="onActivate"
        @keydown.space.stop.prevent="onActivate"
      ></v-icon>
    </template>
    <slot>
      <div>{{ text }}</div>
    </slot>
  </v-tooltip>
  <!-- No reason to show: keep the padlock, but do not open an empty tooltip. -->
  <v-icon v-else :icon="icon" :color="color" v-bind="$attrs"></v-icon>
</template>

<style lang="scss" scoped>
.permission-lock--actionable {
  cursor: pointer;
}
</style>
